import React, { useState } from 'react';
import { Alert, Button, Card, Form, Offcanvas } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCreditCard,
  faCheckCircle,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import yup from 'validation.yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { WizardInput } from 'components/wizard';
import LoadingButton from 'components/common/LoadingButton';
import SoftBadge from 'components/common/SoftBadge';
import { useAuthStore } from 'stores/auth.store';
import driverServices from 'services/driver.services';

const schema = yup
  .object({
    label: yup.string().required(),
    registration_code: yup.string().required()
  })
  .required();

// Stripe reports a reader as online once it has checked in; anything else is worth calling out rather
// than showing in the same colour as a healthy reader.
const READER_ONLINE_STATUS = 'online';

export default function RegisterTerminalReader({ show, onHide, ...props }) {
  const { userData } = useAuthStore();
  const region = userData?.stripeOnboardingBasedCountry;

  const [submitting, setSubmitting] = useState(false);
  const [registeredReader, setRegisteredReader] = useState(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm({ resolver: yupResolver(schema) });

  const onSubmit = async formValue => {
    setSubmitting(true);
    try {
      // The response interceptor already unwraps to the body, so the reader fields are top level —
      // the same shape the hardware shop reads publishable_key from.
      const reader = await driverServices.registerTerminalReader(region, formValue);
      setRegisteredReader(reader);
      reset();
      toast.success('Terminal reader registered.');
    } catch (err) {
      // A 400 travels down here untouched; only 5xx and network errors get the interceptor's generic
      // toast. Read the backend message so the driver sees why the registration was refused.
      toast.error(err?.error?.message || 'Could not register the terminal reader.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Offcanvas
      show={show}
      onHide={onHide}
      {...props}
      placement="end"
      backdropClassName="custom-drawer"
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title className="d-flex align-items-center">
          <FontAwesomeIcon icon={faCreditCard} className="me-2 text-primary" />
          <span>Register Terminal Reader</span>
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body className="p-0 d-flex flex-column">
        <div className="p-3 flex-1">
          {/* Form card — mirrors the create-driver card language (subtle header, bordered body). */}
          <Card className="mb-3">
            <Card.Header className="bg-primary-subtle">
              <h5 className="mb-0 fs-0">Reader details</h5>
            </Card.Header>
            <Card.Body className="border-top fs--1">
              <p className="fs--1 text-muted">
                Turn the reader on and it shows a registration code on its screen. Enter that code
                together with a label you will recognise the reader by, and it will be added to your
                account.
              </p>

              {/* Without a region the request would carry an empty x-region header, which the
                  router silently treats as AU — a US or GB reader would then be registered on the
                  wrong market's platform account, with no error to show for it. */}
              {!region ? (
                <Alert variant="warning" className="fs--1 d-flex align-items-start mb-0">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="me-2 mt-1" />
                  <span>
                    Your account has no onboarding country yet, so we cannot tell which region to
                    register the reader in. Please contact support.
                  </span>
                </Alert>
              ) : (
                <Form id="register-reader-form" onSubmit={handleSubmit(onSubmit)}>
                  <WizardInput
                    label="Label"
                    required
                    name="label"
                    errors={errors}
                    register={register}
                    formGroupProps={{ className: 'mb-3' }}
                    formControlProps={{
                      ...register('label'),
                      placeholder: 'Front desk reader',
                      style: { maxWidth: 320 }
                    }}
                  />
                  <WizardInput
                    label="Registration Code"
                    required
                    name="registration_code"
                    errors={errors}
                    register={register}
                    formGroupProps={{ className: 'mb-2' }}
                    formControlProps={{
                      ...register('registration_code'),
                      placeholder: 'quick-brown-fox',
                      style: { maxWidth: 320 }
                    }}
                  />
                  <Form.Text className="text-muted d-block mb-0 fs--2">
                    Shown on the reader&apos;s screen after you switch it on.
                  </Form.Text>
                </Form>
              )}
            </Card.Body>
          </Card>

          {registeredReader && (
            <Card className="mb-0">
              <Card.Header className="bg-primary-subtle d-flex align-items-center">
                <FontAwesomeIcon icon={faCheckCircle} className="text-success me-2" />
                <h5 className="mb-0 fs-0">Registered reader</h5>
              </Card.Header>
              <Card.Body className="border-top fs--1">
                <dl className="mb-0">
                  <div className="d-flex justify-content-between mb-2">
                    <dt className="text-muted fw-normal">Label</dt>
                    <dd className="mb-0 text-900">{registeredReader.label || '-'}</dd>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <dt className="text-muted fw-normal">Serial number</dt>
                    <dd className="mb-0 text-900">{registeredReader.serial_number || '-'}</dd>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <dt className="text-muted fw-normal">Device type</dt>
                    <dd className="mb-0 text-900">{registeredReader.device_type || '-'}</dd>
                  </div>
                  <div className="d-flex justify-content-between align-items-center">
                    <dt className="text-muted fw-normal">Status</dt>
                    <dd className="mb-0">
                      <SoftBadge
                        bg={
                          registeredReader.status === READER_ONLINE_STATUS ? 'success' : 'warning'
                        }
                        pill
                      >
                        {registeredReader.status || 'unknown'}
                      </SoftBadge>
                    </dd>
                  </div>
                </dl>
              </Card.Body>
            </Card>
          )}
        </div>

        {/* Footer action bar — the button sits outside the form and reaches it via form="…". */}
        {region && (
          <div className="d-flex justify-content-end gap-2 px-3 py-2 border-top bg-body-quaternary">
            <LoadingButton
              name="Register Reader"
              isLoading={submitting}
              loadingBtnName="Registering"
              size="sm"
              btnProps={{ type: 'submit', form: 'register-reader-form', variant: 'primary' }}
            />
            <Button variant="falcon-default" size="sm" onClick={onHide}>
              Close
            </Button>
          </div>
        )}
      </Offcanvas.Body>
    </Offcanvas>
  );
}
